import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import {
  ActivitiesPageContainer,
  Section,
  SectionTitle,
  ActivitiesList,
  ActivityCard,
  ActivityTitle,
  ActivityMeta,
} from '../styles/ChurchActivitiesPageStyles';

const CACHE_KEY = 'activities_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const sortActivities = (list) => {
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const ao = typeof a.displayOrder === 'number' ? a.displayOrder : 0;
    const bo = typeof b.displayOrder === 'number' ? b.displayOrder : 0;
    return ao - bo;
  });
};

const ChurchActivitiesPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/activities');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load activities data.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <ActivitiesPageContainer>
        <LoadingSpinner />
      </ActivitiesPageContainer>
    );
  }

  if (error && !pageData) {
    return <ActivitiesPageContainer>{error}</ActivitiesPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const overviewText = pageData ? pageData.overviewText : null;
  const weeklyActivities = sortActivities(pageData && pageData.weeklyActivities);
  const monthlyActivities = sortActivities(pageData && pageData.monthlyActivities);
  const yearlyActivities = sortActivities(pageData && pageData.yearlyActivities);

  const renderActivitiesSection = (title, list) => {
    if (!list || list.length === 0) return null;
    return (
      <Section>
        <SectionTitle>{title}</SectionTitle>
        <ActivitiesList>
          {list.map((item, index) => (
            <ActivityCard key={`${item.title || 'activity'}-${index}`}>
              {item.title && <ActivityTitle>{item.title}</ActivityTitle>}
              {(item.day || item.time) && (
                <ActivityMeta>
                  {item.day && <span>{item.day}</span>}
                  {item.day && item.time && <span> • </span>}
                  {item.time && <span>{item.time}</span>}
                </ActivityMeta>
              )}
              {item.description && <p>{item.description}</p>}
            </ActivityCard>
          ))}
        </ActivitiesList>
      </Section>
    );
  };

  return (
    <ActivitiesPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {overviewText && (
        <Section>
          <SectionTitle>Overview</SectionTitle>
          <p>{overviewText}</p>
        </Section>
      )}

      {renderActivitiesSection('Weekly Activities', weeklyActivities)}
      {renderActivitiesSection('Monthly Activities', monthlyActivities)}
      {renderActivitiesSection('Yearly Activities', yearlyActivities)}
    </ActivitiesPageContainer>
  );
};

export default ChurchActivitiesPage;
